import React from 'react';
import { Table, Pagination, Button, Tooltip, Space } from 'antd';
import { DownloadOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import { Task, TaskTableProps } from './types';

const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onDownload,
  onRetry,
  onEditRetry
}) => {
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '-';
    const d = new Date(timeStr);
    return d.toLocaleString();
  };

  const columns = [
    {
      title: '任务ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text?.slice(0, 10)}{text && text.length > 10 ? '...' : ''}</span>
        </Tooltip>
      )
    },
    {
      title: '文件名',
      dataIndex: 'original_filename',
      key: 'original_filename',
      width: 150,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text?.slice(0, 15)}{text && text.length > 15 ? '...' : ''}</span>
        </Tooltip>
      )
    },
    {
      title: '任务类型',
      dataIndex: 'task_type',
      key: 'task_type',
      width: 100,
      render: (text: string) => {
        const map: { [key: string]: string } = { 
          translation: '翻译', 
          language_detection: '语种识别' 
        };
        return <span>{map[text] || text}</span>;
      }
    },
    {
      title: '目标语言',
      dataIndex: 'target_language',
      key: 'target_language',
      width: 100,
    },
    {
      title: '处理列',
      dataIndex: 'translate_columns',
      key: 'translate_columns',
      width: 120,
      render: (columns: string[]) => (
        <Tooltip title={columns?.join(', ')} placement="topLeft">
          <span>{columns?.join(', ')?.slice(0, 15)}{columns?.join(', ') && columns.join(', ').length > 15 ? '...' : ''}</span>
        </Tooltip>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text: string, record: Task) => {
        let color = '#999';
        if (text === 'completed') color = '#52c41a';
        if (text === 'processing') color = '#1890ff';
        if (text === 'pending') color = '#faad14';
        if (text === 'failed') color = '#ff4d4f';
        const map: { [key: string]: string } = { 
          completed: '已完成', 
          processing: '处理中', 
          pending: '待处理', 
          failed: '失败' 
        };
        
        if (text === 'failed' && record.error_message) {
          return (
            <Tooltip title={`失败原因：${record.error_message}`} placement="topLeft">
              <span style={{ color, cursor: 'help' }}>{map[text] || text}</span>
            </Tooltip>
          );
        }
        
        return <span style={{ color }}>{map[text] || text}</span>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: formatTime
    },
    {
      title: '完成时间',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 160,
      render: formatTime
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: Task) => (
        <Space>
          {record.status === 'completed' && (
            <Button icon={<DownloadOutlined />} size="small" onClick={() => onDownload(record.id)}>
              下载
            </Button>
          )}
          {record.status === 'failed' && (
            <>
              <Button icon={<ReloadOutlined />} size="small" onClick={() => onRetry(record.id)}>
                重试
              </Button>
              <Button icon={<EditOutlined />} size="small" onClick={() => onEditRetry(record)}>
                编辑重试
              </Button>
            </>
          )}
          {record.status === 'pending' || record.status === 'processing' ? (
            <span style={{ color: '#ccc' }}>-</span>
          ) : null}
        </Space>
      )
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={tasks}
        pagination={false}
        bordered
        rowKey="id"
        scroll={{ x: 1200 }}
        size="middle"
        loading={loading}
      />
      
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          current={page}
          total={total}
          pageSize={pageSize}
          showSizeChanger
          pageSizeOptions={["10", "30", "50"]}
          onChange={onPageChange}
          onShowSizeChange={onPageChange}
        />
      </div>
    </>
  );
};

export default TaskTable;
